/**
 * Search run — the frozen snapshot: funnel stats as materialized, the member
 * list ("the list"), new-since-last-run, CSV download, open-in-board, drill-ins
 * recomputed from the frozen definition. Polls while queued/running.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bot, ChevronDown, ChevronRight, Download, ExternalLink, Loader2, Pencil, Play, SearchCheck, Send } from 'lucide-react';
import api from '../../../services/api';
import { LifecycleBadge, PageHeader, RefreshButton } from '../../components';
import { FilterChip } from '../../components/filters';
import FunnelRail from '../../components/search/FunnelRail';
import StepDrillIn from '../../components/search/StepDrillIn';
import ExportModal from '../../components/search/ExportModal';
import AgentBatchLauncher from '../../components/agent/AgentBatchLauncher';
import { Alert } from '../../components/ui';
import type { CrmForm, SearchLeadRow, SearchRunCompare, SearchRunSummary, StudyOption } from '../../types';
import { dateTime, prettySlug, relTime, useTeam } from '../../lib';
import { describeStep } from './searchLib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** The header's outline action buttons (Edit search / Open in board / CSV / Export / Agent outreach). */
const outlineBtnCls = 'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0';

export default function SearchRun() {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const { team } = useTeam();

  const [run, setRun] = useState<SearchRunSummary | null>(null);
  const [searchName, setSearchName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<SearchLeadRow[]>([]);
  const [membersTotal, setMembersTotal] = useState<number | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [drillStepId, setDrillStepId] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);

  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [compare, setCompare] = useState<SearchRunCompare | null>(null);
  const [compareOpen, setCompareOpen] = useState<'added' | 'dropped' | null>(null);
  useEffect(() => {
    api.get('/api/crm/studies').then((r) => setStudies(r.data?.studies || [])).catch(() => {});
    api.get('/api/crm/forms').then((r) => setForms(r.data?.forms || [])).catch(() => {});
  }, []);

  const pollCount = useRef(0);
  const loadRun = useCallback(async () => {
    if (!runId) return null;
    try {
      const res = await api.get(`/api/crm/search-runs/${runId}`);
      setRun(res.data?.run || null);
      setSearchName(res.data?.search_name ?? null);
      setError(null);
      return res.data?.run as SearchRunSummary;
    } catch (e: any) {
      setError(errMsg(e));
      return null;
    }
  }, [runId]);

  const loadMembers = useCallback(async (offset: number, replace: boolean, onlyNew: boolean) => {
    if (!runId) return;
    setLoadingMembers(true);
    try {
      const res = await api.get(`/api/crm/search-runs/${runId}/members`, {
        params: { offset, limit: 50, ...(onlyNew ? { new_only: 'true' } : {}) },
      });
      const batch: SearchLeadRow[] = res.data?.members || [];
      setMembers((prev) => (replace ? batch : [...prev, ...batch]));
      setMembersTotal(res.data?.total ?? null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoadingMembers(false);
    }
  }, [runId]);

  // Initial load + poll while the run is executing (runs are seconds-scale).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let alive = true;
    const tick = async () => {
      const r = await loadRun();
      if (!alive) return;
      if (r && (r.status === 'queued' || r.status === 'running') && pollCount.current < 120) {
        pollCount.current += 1;
        timer = setTimeout(tick, 1000);
      } else if (r && r.status === 'complete') {
        loadMembers(0, true, false);
        if (r.search_id) {
          api.get(`/api/crm/search-runs/${r.id}/compare`, { params: { limit: 100 } })
            .then((res) => { if (alive) setCompare(res.data || null); })
            .catch(() => {}); // the strip is additive — a failure just hides it
        }
      }
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [loadRun, loadMembers]);

  const toggleNewOnly = () => {
    const next = !newOnly;
    setNewOnly(next);
    loadMembers(0, true, next);
  };

  const rerun = async () => {
    if (!run) return;
    setRerunning(true);
    try {
      const res = run.search_id
        ? await api.post(`/api/crm/searches/${run.search_id}/run`)
        : await api.post('/api/crm/search/run', { definition: run.definition });
      navigate(`/crm/search/runs/${res.data.run_id}`);
      pollCount.current = 0;
      setMembers([]);
      setMembersTotal(null);
      setCompare(null);
      setCompareOpen(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setRerunning(false);
    }
  };

  const downloadCsv = async () => {
    if (!runId) return;
    setDownloading(true);
    try {
      const res = await api.get(`/api/crm/search-runs/${runId}/members.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `search-list-${runId.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setDownloading(false);
    }
  };

  const openInBoard = () => {
    if (!runId) return;
    const label = searchName || 'Ad-hoc search';
    navigate(`/crm/database?list=${runId}&list_name=${encodeURIComponent(label)}`);
  };

  const editSearch = () => {
    if (run?.search_id) navigate(`/crm/search/${run.search_id}`);
    else if (run?.definition) navigate('/crm/search/new', { state: { seed: run.definition, name: searchName || '' } });
  };

  const ctx = useMemo(() => ({ studies, team, forms }), [studies, team, forms]);
  const stepDefs = run?.definition?.steps || [];
  const stepStats = run?.step_stats || [];
  const drillStats = stepStats.find((s) => s.step_id === drillStepId) || null;
  const drillStep = stepDefs.find((s) => s.id === drillStepId);
  const isTerminal = run && (run.status === 'complete' || run.status === 'failed');

  return (
    <div className="space-y-4">
      <PageHeader
        icon={SearchCheck}
        title={searchName ? `${searchName} — run` : 'Search run'}
        count={run?.final_total ?? null}
        subtitle={run ? `${run.trigger === 'schedule' ? 'Scheduled' : 'Manual'} run · ${dateTime(run.created_at)}${run.duration_ms ? ` · ${(run.duration_ms / 1000).toFixed(1)}s` : ''}` : undefined}
        actions={
          <>
            <button onClick={editSearch} className={outlineBtnCls}>
              <Pencil className="h-4 w-4" />
              <span className="hidden sm:inline">Edit search</span>
            </button>
            <button onClick={openInBoard} disabled={run?.status !== 'complete'} className={outlineBtnCls}>
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">Open in board</span>
            </button>
            <button onClick={downloadCsv} disabled={run?.status !== 'complete' || downloading} className={outlineBtnCls}>
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="hidden sm:inline">CSV</span>
            </button>
            <button onClick={() => setExportOpen(true)} disabled={run?.status !== 'complete' || (run?.final_total ?? 0) === 0} title="Attach this list to a study and optionally schedule a campaign" className={outlineBtnCls}>
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button onClick={() => setAgentOpen(true)} disabled={run?.status !== 'complete' || (run?.final_total ?? 0) === 0} title="Launch a staff-approved agent re-engagement batch over this list (§6.13)" className={outlineBtnCls}>
              <Bot className="h-4 w-4" />
              <span className="hidden sm:inline">Agent outreach</span>
            </button>
            <button onClick={rerun} disabled={rerunning || !isTerminal} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0">
              {rerunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Re-run
            </button>
            <RefreshButton onClick={() => { loadRun(); if (run?.status === 'complete') loadMembers(0, true, newOnly); }} loading={loadingMembers} label="Refresh run" />
          </>
        }
      />

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {!run ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : run.status === 'queued' || run.status === 'running' ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white py-16 dark:border-gray-700 dark:bg-gray-800">
          <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Materializing the list — counting every level…</p>
        </div>
      ) : run.status === 'failed' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <p className="font-medium">This run failed.</p>
          <p className="mt-1">{run.error || 'Unknown error'}</p>
          {String(run.error || '').includes('budget_exceeded') && (
            <p className="mt-1 text-xs">The definition scanned more rows than the safety budget allows — narrow the base population or the first step.</p>
          )}
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
          <div className="space-y-3 xl:sticky xl:top-14 xl:self-start">
            <FunnelRail
              baseTotal={run.base_total}
              steps={stepStats}
              stepDefs={stepDefs}
              finalTotal={run.final_total}
              onStepClick={(sid) => setDrillStepId(sid)}
              activeStepId={drillStepId}
              ctx={ctx}
            />
            <p className="text-xs text-gray-400">
              Snapshot taken {relTime(run.finished_at)} — the list won't shift as data changes; re-run for fresh membership.
              Step drill-ins recompute against current data.
            </p>
          </div>

          <div className="space-y-3">
          {compare?.prev_run && (
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">vs previous run</span>
                <span className="text-xs text-gray-400">{relTime(compare.prev_run.finished_at)} · {compare.prev_run.final_total?.toLocaleString() ?? '—'} leads</span>
                <span className={`font-semibold tabular-nums ${(compare.final_delta ?? 0) > 0 ? 'text-green-600 dark:text-green-400' : (compare.final_delta ?? 0) < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500'}`}>
                  {(compare.final_delta ?? 0) > 0 ? '+' : ''}{compare.final_delta ?? 0}
                </span>
                <button
                  type="button"
                  onClick={() => setCompareOpen(compareOpen === 'added' ? null : 'added')}
                  disabled={!compare.added_count}
                  className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-green-700 hover:bg-green-50 disabled:cursor-default disabled:opacity-60 dark:text-green-400 dark:hover:bg-green-900/20"
                >
                  {compareOpen === 'added' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  {compare.added_count ?? 0} joined
                </button>
                <button
                  type="button"
                  onClick={() => setCompareOpen(compareOpen === 'dropped' ? null : 'dropped')}
                  disabled={!compare.dropped_count}
                  className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-default disabled:opacity-60 dark:text-rose-400 dark:hover:bg-rose-900/20"
                >
                  {compareOpen === 'dropped' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  {compare.dropped_count ?? 0} dropped
                </button>
              </div>
              {compareOpen && (() => {
                const rows = (compareOpen === 'added' ? compare.added : compare.dropped) || [];
                const total = compareOpen === 'added' ? compare.added_count ?? 0 : compare.dropped_count ?? 0;
                return (
                  <ul className="max-h-64 divide-y divide-gray-50 overflow-y-auto border-t border-gray-100 dark:divide-gray-700/50 dark:border-gray-700">
                    {rows.map((r) => (
                      <li key={r.id}>
                        <Link
                          to={`/crm/contacts/${r.id}`}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left no-underline transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        >
                          <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100">
                            {r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.phone_e164 || r.email}
                          </span>
                          <LifecycleBadge status={r.lifecycle_status} />
                          {r.effective_study_slug && <span className="hidden text-xs text-gray-400 lg:inline">{prettySlug(r.effective_study_slug)}</span>}
                        </Link>
                      </li>
                    ))}
                    {total > rows.length && (
                      <li className="px-3 py-1.5 text-center text-xs text-gray-400">first {rows.length} of {total}</li>
                    )}
                    {rows.length === 0 && (
                      <li className="px-3 py-3 text-center text-xs text-gray-400">
                        {compareOpen === 'dropped'
                          ? 'Dropped leads may have merged or been archived since.'
                          : 'Nobody.'}
                      </li>
                    )}
                  </ul>
                );
              })()}
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-700">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                The list — {membersTotal?.toLocaleString() ?? run.final_total?.toLocaleString() ?? '—'} lead{(membersTotal ?? run.final_total) === 1 ? '' : 's'}
              </h2>
              {(run.new_matches || 0) > 0 && (
                <FilterChip active={newOnly} onClick={toggleNewOnly}>
                  {run.new_matches} new since last run
                </FilterChip>
              )}
            </div>
            <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {members.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/crm/contacts/${r.id}`}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left no-underline transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100">
                      {r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.phone_e164 || r.email}
                      {r.is_new && (
                        <span className="ml-1.5 rounded-full bg-green-100 px-1.5 py-0.5 text-[11px] font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">new</span>
                      )}
                    </span>
                    <LifecycleBadge status={r.lifecycle_status} />
                    {r.effective_study_slug && <span className="hidden text-xs text-gray-400 lg:inline">{prettySlug(r.effective_study_slug)}</span>}
                    <span className="hidden text-xs text-gray-400 md:inline">{r.phone_e164 || r.email || ''}</span>
                  </Link>
                </li>
              ))}
              {members.length === 0 && !loadingMembers && (
                <li className="px-3 py-8 text-center text-sm text-gray-400">Empty list.</li>
              )}
            </ul>
            {loadingMembers && <Loader2 className="mx-auto my-3 h-5 w-5 animate-spin text-gray-400" />}
            {membersTotal !== null && members.length < membersTotal && !loadingMembers && (
              <button
                type="button"
                onClick={() => loadMembers(members.length, false, newOnly)}
                className="block w-full border-t border-gray-100 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Load more ({members.length.toLocaleString()} of {membersTotal.toLocaleString()})
              </button>
            )}
          </div>
          </div>
        </div>
      )}

      <StepDrillIn
        open={Boolean(drillStepId)}
        onClose={() => setDrillStepId(null)}
        stepStats={drillStats}
        stepTitle={drillStep ? describeStep(drillStep, ctx) : ''}
        mode="run"
        runId={runId}
        onOpenLead={(cid) => navigate(`/crm/contacts/${cid}`)}
      />

      {exportOpen && run && (
        <ExportModal
          run={run}
          searchName={searchName}
          studies={studies}
          team={team}
          onClose={() => setExportOpen(false)}
        />
      )}

      {agentOpen && run && runId && (
        <AgentBatchLauncher
          runId={runId}
          searchName={searchName}
          studies={studies}
          onClose={() => setAgentOpen(false)}
        />
      )}
    </div>
  );
}
